import { Provider } from '@angular/core';
import { SystemsRepositoryPort } from 'common/systems/application/ports/systems-repository.port';
import { SystemsRealtimePort } from 'common/systems/application/ports/systems-realtime.port';
import { SystemsStatePort } from 'common/systems/application/ports/systems-state.port';
import { LoadSystemsUseCase } from 'common/systems/application/use-cases/load-systems.use-case';
import { SystemsFacade } from 'common/systems/presentation/facades/systems.facade';
import { SystemsRepositoryAdapter } from 'ui/features/systems/infrastructure/adapters/systems-repository.adapter';
import { SystemsRealtimeAdapter } from 'ui/features/systems/infrastructure/adapters/systems-realtime.adapter';
import { SystemsStoreAdapter } from 'ui/features/systems/infrastructure/adapters/systems-store.adapter';
import { SYSTEMS_POLLING_INTERVAL_MS } from 'ui/features/systems/infrastructure/tokens/systems-polling-interval.token';

const DEFAULT_SYSTEMS_POLLING_INTERVAL_MS: number = 60_000;

/**
 * Wires the concrete implementation of the `systems` capability:
 * `SystemsRepositoryPort`/`SystemsRealtimePort` resolve to their adapters
 * over the legacy `SystemsService`/`JsonapiWebsocketService` (now consumed
 * directly by `SystemsStore`, the actual owner of `SystemsState`);
 * `SystemsStatePort` resolves to `SystemsStoreAdapter` via `useExisting`;
 * and `SystemsFacade` (a plain class with no `@Injectable`) is resolved
 * via `useFactory`, depending only on `SystemsStatePort`, following the
 * same pattern as `ACTIVE_DEALS_BAG_PROVIDERS`. No consumer of the
 * capability should instantiate the adapters directly.
 */
export const SYSTEMS_PROVIDERS: Array<Provider> = [
    {
        provide: SystemsRepositoryPort,
        useClass: SystemsRepositoryAdapter,
    },
    {
        provide: SystemsRealtimePort,
        useClass: SystemsRealtimeAdapter,
    },
    {
        provide: SYSTEMS_POLLING_INTERVAL_MS,
        useValue: DEFAULT_SYSTEMS_POLLING_INTERVAL_MS,
    },
    SystemsStoreAdapter,
    {
        provide: SystemsStatePort,
        useExisting: SystemsStoreAdapter,
    },
    {
        provide: LoadSystemsUseCase,
        useFactory: (
            repositoryPort: SystemsRepositoryPort,
            statePort: SystemsStatePort,
        ): LoadSystemsUseCase => new LoadSystemsUseCase(repositoryPort, statePort),
        deps: [SystemsRepositoryPort, SystemsStatePort],
    },
    {
        provide: SystemsFacade,
        useFactory: (
            statePort: SystemsStatePort,
            loadSystemsUseCase: LoadSystemsUseCase,
        ): SystemsFacade => new SystemsFacade(statePort, loadSystemsUseCase),
        deps: [SystemsStatePort, LoadSystemsUseCase],
    },
];
